'use client'

import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { DEFAULT_REGION, REGION_CHANGED_EVENT, currentRegion, regionCookie, type Region } from '@/lib/region'

interface RegionState {
  region: Region
  setRegion: (next: Region) => void
}

const RegionContext = createContext<RegionState>({ region: DEFAULT_REGION, setRegion: () => {} })

function subscribe(onChange: () => void): () => void {
  window.addEventListener(REGION_CHANGED_EVENT, onChange)
  return () => window.removeEventListener(REGION_CHANGED_EVENT, onChange)
}

/** The region lives in a cookie (read by every API call); this keeps what the UI shows in step with it. */
export function RegionProvider({ children }: { children: ReactNode }) {
  // The server render has no cookie to read, so it renders Colombia and the client takes over on hydration.
  const region = useSyncExternalStore(subscribe, currentRegion, () => DEFAULT_REGION)
  const setRegion = useCallback((next: Region) => {
    document.cookie = regionCookie(next, process.env.NEXT_PUBLIC_BASE_PATH ?? '')
    window.dispatchEvent(new CustomEvent(REGION_CHANGED_EVENT, { detail: next }))
  }, [])
  const value = useMemo(() => ({ region, setRegion }), [region, setRegion])
  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
}

export function useRegion(): RegionState {
  return useContext(RegionContext)
}
